import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Twitter, Facebook, Youtube } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { COMPANY_INFO } from '../data/companyData';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_177ec162-a789-4cad-ab14-accead9ebd01/artifacts/53213un4_Logo%20Meraki.png";

const Footer = () => {
  const { language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { label: { es: 'Inicio', en: 'Home' }, path: '/' },
    { label: { es: 'Nosotros', en: 'About' }, path: language === 'es' ? '/nosotros' : '/about' },
    { label: { es: 'Servicios', en: 'Services' }, path: language === 'es' ? '/servicios' : '/services' },
    { label: { es: 'Talento', en: 'Talent' }, path: language === 'es' ? '/talento' : '/talent' },
    { label: { es: 'Contacto', en: 'Contact' }, path: language === 'es' ? '/contacto' : '/contact' },
  ];

  const socialLinks = [
    { icon: Instagram, href: COMPANY_INFO.social.instagram },
    { icon: Linkedin, href: COMPANY_INFO.social.linkedin },
    { icon: Twitter, href: COMPANY_INFO.social.twitter },
    { icon: Facebook, href: COMPANY_INFO.social.facebook },
    { icon: Youtube, href: COMPANY_INFO.social.youtube },
  ];

  return (
    <footer className="bg-[#050505] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/">
              <img src={LOGO_URL} alt="MERAKI" className="h-12 w-auto mb-6" />
            </Link>
            <p className="text-zinc-500 text-sm mb-4">{COMPANY_INFO.legalName}</p>
            <p className="text-zinc-600 text-xs">RFC: {COMPANY_INFO.rfc}</p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-semibold uppercase tracking-wider text-sm mb-6">
              {language === 'es' ? 'Navegación' : 'Navigation'}
            </h4>
            <nav className="space-y-3">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className="block text-sm text-zinc-500 hover:text-[#D91616] transition-colors">
                  {link.label[language]}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold uppercase tracking-wider text-sm mb-6">
              {language === 'es' ? 'Contacto' : 'Contact'}
            </h4>
            <div className="space-y-3 text-sm text-zinc-500">
              <p>{COMPANY_INFO.contact.phone}</p>
              <p>{COMPANY_INFO.contact.email}</p>
              <p className="pt-2">{COMPANY_INFO.address.street}</p>
              <p>{COMPANY_INFO.address.floor}</p>
              <p>{COMPANY_INFO.address.colony}</p>
              <p>{COMPANY_INFO.address.city}, {COMPANY_INFO.address.zip}</p>
            </div>
          </div>

          {/* Social & Legal */}
          <div>
            <h4 className="text-white font-semibold uppercase tracking-wider text-sm mb-6">
              {language === 'es' ? 'Síguenos' : 'Follow Us'}
            </h4>
            <div className="flex gap-3 mb-8">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/5 flex items-center justify-center text-zinc-400 hover:bg-[#D91616] hover:text-white transition-all"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
            
            <div className="space-y-2">
              <a href="#" className="block text-sm text-zinc-500 hover:text-[#D91616]">
                {language === 'es' ? 'Aviso de Privacidad' : 'Privacy Policy'}
              </a>
              <a href="#" className="block text-sm text-zinc-500 hover:text-[#D91616]">
                {language === 'es' ? 'Términos y Condiciones' : 'Terms & Conditions'}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-zinc-600">
            © {currentYear} {COMPANY_INFO.name}. {language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
          </p>
          <p className="text-xs text-zinc-700">
            {COMPANY_INFO.address.city}, México • Global Operations
          </p>
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-[#D91616]/50 to-transparent" />
    </footer>
  );
};

export default Footer;
